import React, { useRef, useState } from "react";
import OcButton from "../components/OcButton";
import WaitlistModal from "../components/WaitlistModal";
import useOcReveal from "../useOcReveal";
import "./OcMinds.scss";

import iconLearning from "../../../assets/images/home/04-oc-minds/icon-learning-coach.svg";
import iconEnglish from "../../../assets/images/home/04-oc-minds/icon-english-writing-tutor.svg";
import iconGeneral from "../../../assets/images/home/04-oc-minds/icon-general-assistant.svg";
import iconCreate from "../../../assets/images/home/04-oc-minds/icon-create-your-own.svg";

import imgLearning from "../../../assets/images/home/04-oc-minds/oc-minds-learning-coach.png";
import imgEnglish from "../../../assets/images/home/04-oc-minds/oc-minds-english-coach.png";
import imgGeneral from "../../../assets/images/home/04-oc-minds/oc-minds-general-assistant.png";
import imgCreate from "../../../assets/images/home/04-oc-minds/oc-minds-create-your-own.png";

const TABS = [
  {
    label: "Learning Coach",
    icon: iconLearning,
    img: imgLearning,
    desc: "Helps you understand difficult concepts, organize study plans, and stay on track with your learning goals. Good for homework, exam prep, and building better study habits.",
  },
  {
    label: "English Writing Tutor",
    icon: iconEnglish,
    img: imgEnglish,
    desc: "Sharpen your writing with feedback on grammar, structure, and clarity. Great for essays, emails, and building confidence in written English.",
  },
  {
    label: "General Assistant",
    icon: iconGeneral,
    img: imgGeneral,
    desc: "Your everyday AI partner for questions, planning, and getting things done, always aware of your goals and the context you're working in.",
  },
  {
    label: "Create your own",
    icon: iconCreate,
    img: imgCreate,
    desc: "Build a custom Mind tailored to what you need. Define its focus, tone, and knowledge so it helps with exactly the work that matters to you.",
  },
];

export default function OcMinds() {
  const [active, setActive] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [hasSwitched, setHasSwitched] = useState(false);
  const sectionRef = useRef(null);
  useOcReveal(sectionRef);
  const tab = TABS[active];

  return (
    <section className="ocminds" id="oc-minds" ref={sectionRef}>
      <div className="ocminds__inner oc-reveal">
        <div className="ocminds__heading">
          <h2>OC Minds, built around you</h2>
          <p>
            Your goals, your challenges, your terms. Your Mind shapes itself around what you want to
            solve and helps you actually get it done.
          </p>
        </div>

        <div className="ocminds__content">
          <div className="ocminds__tabs" role="tablist">
            {TABS.map((t, i) => (
              <button
                key={t.label}
                role="tab"
                aria-selected={i === active}
                className={`ocminds-tab${i === active ? " ocminds-tab--active" : ""}`}
                onClick={(e) => {
                  if (i !== active) {
                    setActive(i);
                    setHasSwitched(true);
                  }
                  // On phones the tab row is a horizontal scroller and a tapped
                  // pill can be partially cut off at either edge — slide the row
                  // (never the page) so the active pill is fully visible.
                  const el = e.currentTarget;
                  const row = el.parentElement;
                  const pad = 24;
                  // Rect-based (offsetLeft would be relative to the positioned
                  // ancestor, not the scroller, and be skewed by its margins).
                  const elRect = el.getBoundingClientRect();
                  const rowRect = row.getBoundingClientRect();
                  const leftInRow = elRect.left - rowRect.left + row.scrollLeft;
                  let target = null;
                  if (leftInRow - pad < row.scrollLeft) {
                    target = leftInRow - pad;
                  } else if (leftInRow + elRect.width + pad > row.scrollLeft + row.clientWidth) {
                    target = leftInRow + elRect.width + pad - row.clientWidth;
                  }
                  if (target !== null) {
                    // rAF animation instead of scrollTo({behavior:"smooth"}) —
                    // native smooth scrolls get cancelled by the page's scroll
                    // handling and silently no-op on this container.
                    const from = row.scrollLeft;
                    const delta = target - from;
                    const start = performance.now();
                    const DURATION = 250;
                    const step = (now) => {
                      const t = Math.min(1, (now - start) / DURATION);
                      const eased = 1 - Math.pow(1 - t, 3);
                      row.scrollLeft = from + delta * eased;
                      if (t < 1) requestAnimationFrame(step);
                    };
                    requestAnimationFrame(step);
                  }
                }}
              >
                {/* Icon is masked so its colour follows the tab state (white when
                    active, dark blue when inactive) regardless of the SVG's own
                    baked stroke colour. */}
                <span
                  className="ocminds-tab__icon"
                  style={{
                    WebkitMaskImage: `url(${t.icon})`,
                    maskImage: `url(${t.icon})`,
                  }}
                  aria-hidden="true"
                />
                <span className="ocminds-tab__label">{t.label}</span>
                {i === active && <span className="ocminds-tab__dot" />}
              </button>
            ))}
          </div>

          <div className={`ocminds__panel${hasSwitched ? " ocminds__panel--swapping" : ""}`}>
            <div className="ocminds__panel-img">
              <img key={tab.img} src={tab.img} alt={tab.label} />
            </div>
            <div className="ocminds__panel-foot">
              <div className="ocminds__panel-text" key={tab.label}>
                <p className="ocminds__panel-title">{tab.label}</p>
                <p className="ocminds__panel-desc">{tab.desc}</p>
              </div>
              <OcButton
                variant="primary"
                className="ocminds__panel-btn"
                onClick={() => setWaitlistOpen(true)}
              >
                Get early access
              </OcButton>
            </div>
          </div>
        </div>
      </div>

      <WaitlistModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        variant="minds"
      />
    </section>
  );
}
